/**
 * Shared validation constants for information-block image uploads.
 *
 * Imported by BOTH the client editor (`InformationBlockEditor`) and the offline
 * upload handler so a client-side reject is byte-for-byte identical to what the
 * server would have returned. Do not hand-duplicate these values: the
 * "a client reject is identical to a server reject" guarantee depends on a
 * single source of truth.
 *
 * @module lib/information-blocks/constants
 */

/** Maximum upload size for an information-block image: 15MB. */
export const INFORMATION_BLOCK_MAX_BYTES = 15 * 1024 * 1024;

/**
 * Accepted image MIME types. SVG is deliberately excluded (it is offered in the
 * file picker's accept attribute so it is selectable, then explicitly rejected
 * with a visible message rather than silently hidden).
 */
export const INFORMATION_BLOCK_ALLOWED_MIME_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
] as const;

/** A MIME type accepted for an information-block image. */
export type InformationBlockMimeType = (typeof INFORMATION_BLOCK_ALLOWED_MIME_TYPES)[number];

/**
 * Reject message shown when the dropped file is not an accepted image type
 * (including the deliberately-rejected SVG case). Single canonical string.
 */
export const INFORMATION_BLOCK_MIME_REJECT_MESSAGE = 'Drop a PNG, JPEG, or WEBP image';

/**
 * Reject message shown when the dropped file exceeds the size cap.
 *
 * @param maxBytes The cap to phrase the message around. Defaults to the shared
 *   {@link INFORMATION_BLOCK_MAX_BYTES} so client and server agree.
 */
export function informationBlockSizeRejectMessage(
  maxBytes: number = INFORMATION_BLOCK_MAX_BYTES,
): string {
  const mb = Math.round(maxBytes / (1024 * 1024));
  return `Image is over ${mb}MB. Drop a smaller one`;
}

/**
 * Type guard: true when `mime` is an accepted information-block image type.
 *
 * @param mime The candidate MIME string (e.g. from a `File.type`).
 */
export function isAllowedInformationBlockMime(mime: string): mime is InformationBlockMimeType {
  return (INFORMATION_BLOCK_ALLOWED_MIME_TYPES as readonly string[]).includes(mime);
}

/** Map of accepted MIME type to its canonical file extension. */
const MIME_TO_EXTENSION: Record<InformationBlockMimeType, 'png' | 'jpg' | 'webp'> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
};

/**
 * File extension for a validated MIME type. The R2 key extension is derived from
 * the MIME, not the uploaded filename, so the storage key is deterministic.
 *
 * @param mime A validated {@link InformationBlockMimeType}.
 */
export function extensionForInformationBlockMime(
  mime: InformationBlockMimeType,
): 'png' | 'jpg' | 'webp' {
  return MIME_TO_EXTENSION[mime];
}
